import { useGetCoursesQuery } from "@api/course";
import { useGetUniversitiesQuery } from "@api/university";
import { IconWithTooltip } from "@components/IconWithTooltip";
import { useModal } from "@components/Modal";
import { FunnelIcon } from "@heroicons/react/24/outline";
import { useIsUserRole } from "@hooks/auth";
import { CourseUserDetailsDTO } from "@onetutor/shared/dto";
import Fuse from "fuse.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface SearchCoursesModalProps {
  title?: string;
  excludeCourseIds?: string[];
  children: (course: CourseUserDetailsDTO) => React.ReactNode;
}

export function useSearchCoursesModal({
  title,
  excludeCourseIds = [],
  children,
}: SearchCoursesModalProps) {
  const { Modal, openModal } = useModal();
  const { t } = useTranslation(["homePage", "coursePage", "commons"]);
  const isAdmin = useIsUserRole("admin");

  const SearchCoursesModal = useMemo(
    () =>
      function SearchCoursesModalInner() {
        const [searchQuery, setSearchQuery] = useState("");
        const inputRef = useRef<HTMLInputElement>(null);
        const [selectedUniversityId, setSelectedUniversityId] = useState<
          string | null
        >(null);

        const { data: courses = [] } = useGetCoursesQuery({});
        const { data: universities } = useGetUniversitiesQuery(undefined, {
          skip: !isAdmin,
        });

        const fuse = useMemo(
          () =>
            new Fuse(courses, {
              threshold: 0.4,
              keys: ["name", "lecturer.firstName", "lecturer.lastName"],
            }),
          [courses],
        );

        const filteredCourses = useMemo(() => {
          if (searchQuery === "") {
            return courses;
          }
          return fuse.search(searchQuery).map((result) => result.item);
        }, [searchQuery, courses, fuse]);

        useEffect(() => {
          inputRef.current?.focus();
        }, []);

        const sortedCourses = useMemo(
          () =>
            [...filteredCourses].sort((a, b) => a.name.localeCompare(b.name)),
          [filteredCourses],
        );

        const renderCourseCard = (course: CourseUserDetailsDTO) => {
          return <div key={course.id}>{children(course)}</div>;
        };

        return (
          <Modal
            title={title || t("searchCoursesTitle")}
            isWider
            onCancel={() => {
              setSearchQuery("");
              setSelectedUniversityId(null);
            }}
            hideActionButtons
          >
            <div className="my-2 flex h-[62vh] flex-col gap-y-2 sm:my-4 sm:gap-y-3">
              <input
                type="text"
                ref={inputRef}
                placeholder={t("searchbarPlaceholder")}
                className="input w-full shrink-0"
                value={searchQuery}
                onChange={(e) => {
                  e.stopPropagation();
                  setSearchQuery(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />

              {isAdmin && (
                <div className="flex w-full flex-row items-center">
                  <IconWithTooltip
                    icon={<FunnelIcon className="h-5 w-5 sm:h-7 sm:w-7" />}
                    tooltip={t("filter")}
                    className="btn btn-ghost btn-sm rounded-lg"
                  />
                  <select
                    className="select w-full"
                    value={selectedUniversityId ?? ""}
                    onChange={(e) => {
                      e.stopPropagation();
                      setSelectedUniversityId(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <option key="none" value="">
                      {t("filterAllUniversities")}
                    </option>
                    {universities?.map((uni) => (
                      <option key={uni.id} value={uni.id}>
                        {`${uni.fullName} (${uni.shortName})`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="divider my-2 sm:m-4" />

              <div className="flex w-full flex-1 flex-col gap-y-2 overflow-y-auto overflow-x-hidden sm:gap-y-3">
                {sortedCourses
                  .filter((c) =>
                    selectedUniversityId
                      ? c.university?.id === selectedUniversityId
                      : true,
                  )
                  .filter((c) => !excludeCourseIds.includes(c.id))
                  .map(renderCourseCard)}
              </div>
            </div>
          </Modal>
        );
      },
    [Modal, t, isAdmin, title, excludeCourseIds, children],
  );

  return { SearchCoursesModal, openModal };
}
